import { FC, memo } from 'react'
import styled from 'styled-components'
import { transparentize } from 'polished'
import useInView from 'react-cool-inview'
import { ID } from 'graphql-ws'
import { Delivery, Product, PromoCode } from '@/network/graphql.g'
import { Box } from '@/components/atoms/Grid'
import { MediumTinyBold, Tiny2 } from '@/components/atoms/typography'
import { useMhub } from '@/providers/mhubProvider'
import { shortenValue } from '@/components/molecules/productSizeBox'
import { usePromoAccentColors } from '../../../hooks/usePromoAccentColors'

type ProductDetailSizeBoxNewProps = {
  id: ID
  value: string
  delivery: Delivery
  available: number
  isStock: boolean
  isSelected: boolean
  hasError?: boolean
  product: Product
  position: number
  promoCodes: PromoCode[]
  onClick: () => void
}

const ProductDetailSizeBoxNew: FC<ProductDetailSizeBoxNewProps> = ({
  id,
  value,
  delivery,
  available,
  isStock,
  isSelected,
  hasError = false,
  product,
  position,
  promoCodes,
  onClick
}) => {
  const { pushImpression } = useMhub()
  const colors = usePromoAccentColors()

  const { observe } = useInView({
    unobserveOnEnter: true,
    threshold: [0.5],
    onEnter: () => {
      pushImpression(
        {
          ...product,
          variantId: id,
          variant: value,
          isStock,
          available,
          deliveryTime: delivery
        },
        position,
        `list.detail.size_variants.${product.id}-${product?.MHubProperties?.name}`
      )
    }
  })

  const getPromoCode = () => {
    if (!promoCodes?.length) {
      return null
    }

    if (promoCodes.length === 1) {
      return promoCodes[0]
    }

    const sortedCodes = [...promoCodes].sort(
      (a, b) => (b.discountValue || 0) - (a.discountValue || 0)
    )

    return isStock ? sortedCodes[0] : sortedCodes[sortedCodes.length - 1]
  }

  const currentPromoCode = getPromoCode()
  const hasPromoPrice = !!product?.prices?.promoCodePrice
  const shouldShowDiscount = currentPromoCode?.discountValue && hasPromoPrice

  return (
    <div ref={observe}>
      <SizeBox
        isSelected={isSelected}
        hasError={hasError}
        onClick={onClick}
        data-testid="productDetailSizeBox"
      >
        {shouldShowDiscount ? (
          <DiscountBadge
            colors={colors}
          >{`-${currentPromoCode?.discountValue}%`}</DiscountBadge>
        ) : null}
        <SizeValue isSelected={isSelected} m={0}>
          {shortenValue(value)}
        </SizeValue>
      </SizeBox>
    </div>
  )
}

export default memo(ProductDetailSizeBoxNew)

const SizeBox = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  box-sizing: border-box;
  border-radius: 4px;
  cursor: pointer;
  border: ${(props) =>
    props.isSelected
      ? `1.32px solid ${props.theme.colors.onBackground}`
      : props.hasError
      ? `1px solid ${props.theme.colors.error}`
      : `1px solid ${transparentize(0.8, props.theme.colors.onBackground)}`};
  transition: border-color 0.15s ease-out;
`

const SizeValue = styled(Tiny2)`
  font-size: 12px;
  text-align: center;
  font-weight: ${(props) => (props.isSelected ? 'bold' : 'normal')};
`

const DiscountBadge = styled(MediumTinyBold)`
  position: absolute;
  top: -7px;
  right: -9px;
  margin: 0;
  padding: 1px 2px;
  font-size: 10px;
  border-radius: 3px;
  white-space: nowrap;
  color: ${(props) => props.theme.colors.background};
  background-color: ${(props) => props.colors.dark};
`
